import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { TutorialGamePage } from './TutorialGamePage';
import { TutorialOverlay } from '../components/game/TutorialOverlay';
import { UnlockModal } from '../components/game/UnlockModal';
import { useGameStore } from '../store/gameStore';
import { toast } from '../hooks/use-toast';

// API configuration - shared across all tutorial operations
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE_URL = `${BACKEND_URL}/api`;

/**
 * TutorialPage - Decoupled tutorial system
 * - Same UI/UX as original (floating guide, highlighting, real game components)
 * - BUT uses tutorial-v2 backend with pre-scripted data
 * - No AI calls during tutorial
 * - Independent from main game system
 * - All action processing happens client-side
 */
export const TutorialPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const { updateGameState, addMessage, preSelectedAction, preSelectedTarget } = useGameStore();

  const [currentStep, setCurrentStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [tutorialScript, setTutorialScript] = useState([]);
  const [tutorialNarratives, setTutorialNarratives] = useState({});
  const [theme, setTheme] = useState('90s_nyc');

  // Capture theme on mount to avoid exhaustive-deps warnings
  const initialThemeRef = useRef(location.state?.theme || '90s_nyc');

  // Load tutorial once on mount - doesn't reload on location changes
  useEffect(() => {
    loadTutorial(initialThemeRef.current);
    // Mount-only: intentionally empty deps for one-time load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTutorial = async (selectedTheme) => {
    try {
      setTheme(selectedTheme);

      // Load tutorial data from tutorial-v2 endpoint
      // Get auth token from Clerk
      const token = await getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/tutorial-v2/${selectedTheme}/data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load tutorial: ${response.statusText}`);
      }

      const data = await response.json();
      setTutorialScript(data.tutorialData.script || []);
      setTutorialNarratives(data.narratives || {});

      const innerCaseData = data.tutorialData.case_data;
      const guideCharacter = data.tutorialData.guide_character || innerCaseData.guide_character;

      // Build caseData with victim at top level.
      // killer_id is hoisted out of case_data so AccusationModal's tutorial dropdown
      // lock resolves; the tutorial already names its killer aloud in the instruction
      // box at the accusation step, so this is not a new disclosure.
      const caseDataForStore = {
        ...data.tutorialData,
        victim: innerCaseData.victim,
        killer_id: innerCaseData.killer_id,
      };

      // Initialize game state with tutorial data
      updateGameState({
        caseId: data.tutorialData.id,
        guideCharacter: guideCharacter,
        caseData: caseDataForStore,
        suspects: innerCaseData.suspects,
        clues: innerCaseData.clues,
        locations: innerCaseData.locations,
        documents: innerCaseData.documents,
        discovered: {
          suspects: [],
          clues: [],
          locations: ['myshaps', 'crime_scene'],
          documents: []
        },
        newlyDiscovered: {
          suspects: [],
          clues: [],
          locations: [],
          documents: []
        },
        hintedClues: [],
        messageHistory: [],
        narrativeText: `Welcome! I'm ${guideCharacter?.name || 'your contact'}${guideCharacter?.role ? ', ' + guideCharacter.role : ''}. I'll be walking you through this investigation. Today we're looking into the murder of ${innerCaseData.victim.name}. Let's get started!`,
        currentImage: innerCaseData.locations.find(l => l.id === 'myshaps')?.image || '',
        currentLocation: 'myshaps',
        turnCount: 0,
        consultationsUsed: 0,
        consultationLimit: 5,
        maxTurns: 50,
        // Normalize tutorial theme id to main-game convention ('vice' → 'miami_vice')
        // so TUTORIAL_CIPHER_DATA lookup, TutorialCipherModal theme branches, and
        // RightPanel themeAccent all resolve correctly. Backend URL still uses 'vice'.
        selectedTheme: selectedTheme === 'vice' ? 'miami_vice' : selectedTheme,
        selectedCharacter: guideCharacter ? { ...guideCharacter, type: 'guide' } : null,
        isTutorial: true,
        tutorialStep: 1,
        preSelectedAction: null,
        preSelectedTarget: null,
        sessionId: `tutorial_${selectedTheme}_${Date.now()}` // Fake session ID for tutorial
      });

      // Add opening narrative to messages
      const guideId = guideCharacter?.id || 'guide';
      addMessage('opening', guideId, `Welcome! I'm ${guideCharacter?.name || 'your contact'}${guideCharacter?.role ? ', ' + guideCharacter.role : ''}. I'll be walking you through this investigation.`);

      setCurrentStep(1);
      setLoading(false);

      toast({
        title: 'Tutorial started',
        description: 'Learn the basics of investigation!'
      });

    } catch (error) {
      console.error('Tutorial load error:', error);
      toast({
        title: 'Error',
        description: `Failed to load tutorial: ${error.message}`,
        variant: 'destructive'
      });
      navigate('/');
    }
  };

  const saveProgress = useCallback(async (step) => {
    try {
      const token = await getToken();
      if (!token) {
        console.warn('[TutorialPage.saveProgress] No auth token available, skipping progress save for theme:', theme);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/tutorial-v2/${theme}/progress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ step })
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.warn(`[TutorialPage.saveProgress] Failed to save progress: ${response.status} ${response.statusText}`, responseText);
      }
    } catch (error) {
      console.error('[TutorialPage.saveProgress] Failed to save progress:', error);
      // Don't block tutorial on save failure
    }
  }, [theme, getToken]);

  const handleAdvanceStep = useCallback(async () => {
    // IMPORTANT: Process revelations from current step BEFORE advancing
    // This ensures items are revealed when the Next button is clicked
    const scriptStep = tutorialScript[currentStep - 1];

    if (scriptStep?.reveals) {
      const revealed = scriptStep.reveals;
      // Access discovered imperatively to avoid dependency loop
      const currentDiscovered = useGameStore.getState().discovered || {
        suspects: [],
        clues: [],
        locations: [],
        documents: []
      };

      // Merge revealed items into discovered
      const newDiscovered = {
        suspects: [...new Set([...currentDiscovered.suspects, ...(revealed.suspects || [])])],
        clues: [...new Set([...currentDiscovered.clues, ...(revealed.clues || [])])],
        locations: [...new Set([...currentDiscovered.locations, ...(revealed.locations || [])])],
        documents: [...new Set([...currentDiscovered.documents, ...(revealed.documents || [])])]
      };

      // Update discovered items
      updateGameState({
        discovered: newDiscovered
      });
    }

    const nextStep = currentStep + 1;

    // Update narrative text with next step's narrative
    const nextStepData = tutorialScript[nextStep - 1];
    if (nextStepData?.narrative) {
      updateGameState({
        narrativeText: nextStepData.narrative
      });
    }

    // Cipher step timing varies by theme. Map keys use the frontend convention
    // ('miami_vice'); the raw `theme` from backend is 'vice', so normalize before lookup.
    // Without this, vice falls back to 90s timing and auto-solves the cipher at step 92.
    const cipherSteps = {
      '90s_nyc': { unlock: 88, solve: 92 },
      'noir': { unlock: 98, solve: 102 },
      'miami_vice': { unlock: 96, solve: 100 }
    };
    const normalizedTheme = theme === 'vice' ? 'miami_vice' : theme;
    const themeCipherSteps = cipherSteps[normalizedTheme] || cipherSteps['90s_nyc'];

    // Unlock cipher when cipher button should appear
    if (nextStep === themeCipherSteps.unlock) {
      useGameStore.getState().unlockCipher();
    }

    // Mark cipher as solved after cipher is cracked
    if (nextStep === themeCipherSteps.solve) {
      useGameStore.getState().solveCipher();
    }

    // Show unlock modal when user advances past the final step
    if (nextStep > tutorialScript.length) {
      // Mark tutorial as completed before showing unlock modal
      try {
        const token = await getToken();
        if (token) {
          const response = await fetch(`${API_BASE_URL}/tutorial-v2/${theme}/complete`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (!response.ok) {
            console.error('Failed to mark tutorial complete:', response.status);
          }
        }
      } catch (error) {
        console.error('Failed to mark tutorial complete:', error);
      }
      setShowUnlockModal(true);
      return; // Don't update currentStep past the end
    }

    // Save progress
    await saveProgress(nextStep);

    setCurrentStep(nextStep);
    setMaxStepReached(prev => Math.max(prev, nextStep));
  }, [currentStep, tutorialScript, updateGameState, theme, saveProgress, getToken]);

  // Watch for preSelectedAction changes to advance steps
  useEffect(() => {
    const stepConfig = tutorialScript[currentStep - 1];
    if (!stepConfig?.action || !preSelectedTarget) return;

    // Pattern-based check
    if (stepConfig.action.startsWith('preselect_')) {
      const expectedTarget = stepConfig.action.replace('preselect_', '');
      if (preSelectedTarget === expectedTarget) {
        handleAdvanceStep();
      }
    }
  }, [currentStep, preSelectedAction, preSelectedTarget, handleAdvanceStep, tutorialScript]);

  // Tutorial-specific: Auto-populate action bar for certain steps
  // This ensures the action bar is populated even if the user hasn't clicked the correct element
  useEffect(() => {
    const stepConfig = tutorialScript[currentStep - 1];
    if (!stepConfig) return;

    // Step 66: Consult the Guide - auto-populate action bar with consult_guide
    // This handles the case where step 65 clicked the guide, advanced to step 66,
    // and we need to ensure the action bar is still populated
    if (stepConfig.action === 'action_consult_guide') {
      const currentAction = useGameStore.getState().preSelectedAction;
      const currentTarget = useGameStore.getState().preSelectedTarget;

      // Only set if not already set (avoid infinite loop)
      if (currentAction !== 'consult_guide' || currentTarget !== 'guide') {
        useGameStore.getState().setPreSelectedAction('consult_guide', 'guide');
      }
    }
  }, [currentStep, tutorialScript]);

  // Handle action selection change
  const handleActionSelect = useCallback((action) => {
    const stepConfig = tutorialScript[currentStep - 1];
    if (stepConfig?.action === 'select_examine' && action === 'examine') {
      handleAdvanceStep();
    }
    if (stepConfig?.action === 'select_consult_guide' && action === 'consult_guide') {
      handleAdvanceStep();
    }
  }, [currentStep, handleAdvanceStep, tutorialScript]);

  // Handle panel expansion events
  const handlePanelExpand = useCallback((panelName) => {
    const stepConfig = tutorialScript[currentStep - 1];
    if (!stepConfig?.action) return;

    const expectedAdvanceKey = `panel_expand_${panelName}`;
    if (stepConfig.action === expectedAdvanceKey) {
      handleAdvanceStep();
    }
  }, [currentStep, handleAdvanceStep, tutorialScript]);

  // Handle modal close events (modal opening doesn't trigger tutorial advancement)
  const handleModalClose = useCallback((modalType) => {
    const stepConfig = tutorialScript[currentStep - 1];

    // Handle generic modal_close action (for steps that explicitly wait for modal close)
    if (stepConfig?.action === 'modal_close' && stepConfig?.modalInfo?.closeToAdvance) {
      handleAdvanceStep();
      return;
    }

    // Handle legacy modal closing logic
    if (!stepConfig?.modalInfo?.closeToAdvance) return;

    const modalAdvanceMap = {
      'modal_casefile': 'casefile',
      'modal_evidence': 'evidence',
      'modal_suspects': 'suspects'
    };

    const expectedModal = modalAdvanceMap[stepConfig.action];
    if (expectedModal && modalType === expectedModal) {
      handleAdvanceStep();
    }
  }, [currentStep, handleAdvanceStep, tutorialScript]);

  // Get current step's modal info
  const currentStepModalInfo = tutorialScript[currentStep - 1]?.modalInfo || null;

  // Handle going back
  const handleGoBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleUnlockComplete = () => {
    setShowUnlockModal(false);
    toast({
      title: 'Tutorial Complete!',
      description: 'Congratulations! Case generation is now unlocked.',
      duration: 3000
    });
    navigate('/');
  };

  // Handle action completion from TutorialActionBar
  const handleActionComplete = useCallback((action, _target, _response) => {
    const stepConfig = tutorialScript[currentStep - 1];

    if (!stepConfig?.action || stepConfig.requiresNext) {
      return;
    }

    // Map action values to action types
    const actionMap = {
      'action_travel': 'travel',
      'action_examine': 'examine',
      'action_interview': 'interview',
      'action_read': 'read',
      'action_consult_guide': 'consult_guide',
      'action_accuse': 'accuse',
      'accuse_modal_open': 'accuse',
      'modal_examine_opened': 'modal_examine_opened',
      'focus_selected': 'focus_selected',
      'examination_started': 'examination_started',
      'modal_examine_closed': 'modal_examine_closed',
      'modal_casefile': 'modal_casefile_opened',
      'modal_evidence': 'modal_evidence_opened',
      'modal_suspects': 'modal_suspects_opened',
      'cipher_discovered': 'cipher_discovered',
      'cipher_button_clicked': 'cipher_button_clicked',
      'cipher_solution_entered': 'cipher_solution_entered',
      'cipher_modal_closed': 'cipher_modal_closed',
      'modal_accusation_open': 'accuse_modal_open',
      'accusation_suspect_selected': 'accusation_suspect_selected',
      'accusation_submitted': 'accusation_submitted',
      'modal_summary_open': 'view_summary',
      'summary_closed': 'summary_closed',
      'case_closed': 'case_closed',
      // Interrogation sub-step actions
      'interrogation_approach_selected': 'interrogation_approach_selected',
      'interrogation_begin_clicked': 'interrogation_begin_clicked',
      'interrogation_round1_results': 'interrogation_round1_results',
      'interrogation_continue_clicked': 'interrogation_continue_clicked',
      'interrogation_round2_approach_selected': 'interrogation_round2_approach_selected',
      'interrogation_round2_begin_clicked': 'interrogation_round2_begin_clicked',
      'interrogation_round2_results': 'interrogation_round2_results',
      'interrogation_complete_clicked': 'interrogation_complete_clicked'
    };

    const expectedAction = actionMap[stepConfig.action];
    const mappedAction = actionMap[action] || action;

    if (expectedAction && mappedAction === expectedAction) {
      handleAdvanceStep();
    }
  }, [currentStep, handleAdvanceStep, tutorialScript]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-900">
        <p className="text-stone-100 text-xl">Loading tutorial...</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <TutorialGamePage
        onActionComplete={handleActionComplete}
        onActionSelect={handleActionSelect}
        onModalClose={handleModalClose}
        currentStepModalInfo={currentStepModalInfo}
        onPanelExpand={handlePanelExpand}
        tutorialScript={tutorialScript}
        tutorialNarratives={tutorialNarratives}
        currentStep={currentStep}
      />
      {!showUnlockModal && (
        <TutorialOverlay
          currentStep={currentStep}
          maxStepReached={maxStepReached}
          onAdvance={handleAdvanceStep}
          onGoBack={handleGoBack}
          tutorialScript={tutorialScript}
        />
      )}
      {showUnlockModal && (
        <UnlockModal
          onComplete={handleUnlockComplete}
        />
      )}
    </div>
  );
};

// Export with original name for backward compatibility
export const TutorialPageV2 = TutorialPage;
